<template>
  <ul class="space-y-4 mb-4">
    <li v-for="post in posts" :key="`${[post]['id']}`">
      <div class="cursor-pointer bg-white px-2.5 py-3 sm:p-4 sm:rounded-sm border-gray-200 hover:border-gray-400 border-t border-b sm:border">
        <fandom-post :post="post" :show-fandom="false"></fandom-post>
      </div>
    </li>
  </ul>

  <div class="absolute bg-red-100" style="top: 1100px;">
    <div ref="scrollToTopThreshold"></div>
  </div>

  <div v-if="finished && posts.length === 0" class="text-2xl text-black text-center my-8">
    <span>No posts found.</span>
  </div>
  <div v-if="finished && posts.length !== 0" class="text-2xl text-black text-center my-8">
    <span>All posts loaded!</span>
  </div>

  <div ref="loader" v-if="!finished" class="space-y-4">
    <div class="bg-white px-2.5 py-3 sm:p-4 sm:rounded-sm border-gray-200 border-t border-b sm:border"
         v-for="i in [...Array(3).keys()]" :key="i">
      <div class="animate-pulse">
        <div class="flex items-center space-x-2">
          <div v-if="showFandom" class="flex-shrink-0">
            <div class="rounded-full bg-gray-200 h-7 w-7"></div>
          </div>
          <div class="min-w-0 flex-1 flex items-center space-x-1">
            <div class="h-3.5 w-1/2 rounded bg-gray-200"></div>
          </div>
        </div>
        <div class="mt-5 mb-4 h-5 w-7/12 rounded bg-gray-200"></div>
        <div class="mt-2 space-y-4">
          <div class="h-2.5 w-full rounded bg-gray-200"></div>
          <div class="h-2.5 w-10/12 rounded bg-gray-200"></div>
          <div class="h-2.5 w-11/12 rounded bg-gray-200"></div>
          <div class="h-2.5 w-full rounded bg-gray-200"></div>
        </div>
      </div>
    </div>
  </div>

  <transition
    enter-active-class="transition ease-out duration-100"
    enter-from-class="transform opacity-0 scale-95"
    enter-to-class="transform opacity-100 scale-100"
    leave-active-class="transition ease-in duration-75"
    leave-from-class="transform opacity-100 scale-100"
    leave-to-class="transform opacity-0 scale-95"
  >
    <div ref="scroller" class="hidden fixed bottom-10 right-10 xl:right-1/4">
      <tippy placement="top">
        <div @click="scrollToTop" class="cursor-pointer border border-gray-200 rounded-full p-4 bg-gray-50 hover:bg-gray-100">
          <slot name="upward-arrow-icon"></slot>
        </div>

        <template v-slot:content>
          <div class="nightwind-prevent whitespace-nowrap bg-black text-xs text-white px-2 py-1 rounded-sm">
            <span>Scroll to top</span>
          </div>
        </template>
      </tippy>
    </div>
  </transition>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { Menu, MenuButton, MenuItem, MenuItems, Popover, PopoverButton, PopoverPanel } from "@headlessui/vue"
import { ChatAltIcon, CodeIcon, DotsVerticalIcon, EyeIcon, FlagIcon, PlusIcon, SearchIcon, ShareIcon, StarIcon, ThumbUpIcon } from "@heroicons/vue/solid"
import { BellIcon, MenuIcon, XIcon } from "@heroicons/vue/outline"

import FandomPost from "../fandom/FandomPost.vue"

export default {
  props: {
    userName: { type: String, required: true },
  },

  components: {
    Menu, MenuButton, MenuItem, MenuItems,
    Popover, PopoverButton, PopoverPanel,
    BellIcon, ChatAltIcon, CodeIcon, DotsVerticalIcon, EyeIcon, FlagIcon, MenuIcon, PlusIcon, SearchIcon, ShareIcon, StarIcon, ThumbUpIcon, XIcon,
    FandomPost
  },

  setup(props) {
    const url = ref(`/v1/posts`)
    const posts = ref([])
    const pagy = ref({})
    const finished = ref(false)
    const loader = ref(null)

    const fetchData = async () => {
      if (finished.value) {
        console.debug('Finished loading posts so not fetching')
        return
      }

      let requestUrl = url.value
      requestUrl += `?user_name=${encodeURIComponent(props.userName)}`
      const response = await fetch(requestUrl, {
        headers: { "Accept": "application/json", "Content-Type": "application/json" },
      })
      if (response.ok) {
        const result = await response.json()
        posts.value = posts.value.concat(result["data"])
        console.debug(posts.value)
        pagy.value = result["pagy"]
        if (pagy.value['next'] === null) {
          finished.value = true
        } else {
          url.value = pagy.value["next_url"]
        }
      } else {
        console.error("Something went wrong", response)
      }
    }

    const stopIntersectionObserver = ref(() => {})
    onMounted(async () => {
      await fetchData()

      const { stop, isSupported } = useIntersectionObserver(
          loader,
          ([ { isIntersecting, intersectionRatio } ]) => {
            if (isIntersecting) {
              console.debug('intersecting and fetching data')
              fetchData()
            }
          },
          { rootMargin: "100px" }
      )
      stopIntersectionObserver.value = stop
      if (!isSupported) {
        console.error('Intersection Observer is not supported on this client')
      }
    })
    onUnmounted(() => {
      stopIntersectionObserver()
    })

    return { loader, posts, pagy, finished }
  }
}
</script>